<template>
	<div class="tables-wrapper">

		<AppTable
			ref="businesses-table"
			:table-headings="tableHeadings"
			:title="getString('businesses')"
			:total-items="isAggregateOnly ? aggregateTotalItems : parsedBusinesses.length"
			:items="isAggregateOnly ? aggregateBusinessesData : currentShownBusinesses"
			:fetch-function="isAggregateOnly ? fetchBusinessesPage : dummyFetchFunction"
			:filters="filters"
			:main-color="'#6c33cb'"
			:initial-page-size="10"
			:initial-sort-field="'readable_gross_volume'"
			:initial-sort-asc="false"
			@changed-page="v=>currentPage = v"
			@changed-page-size="v=>pageSize = v"
			@changed-sort="changeSort"
			@changed-filter="changeFilter"
			class="mb-30"
			allow-download
			@export="exportBusinesses"
		/>

		<UserReports v-if="!superStore.isSuper"
			:businesses="businesses"
			:periodDates="periodDates"/>

		<AppNotification :title="getString('warning')" :body-text="getString('table_no_data')" :type="-1" ref="table-no-data" />

	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";

import {useLanguageStore, useScriptStore} from "@/shared/stores";
import {useSuperStore} from "@/user/stores";
import {formatDateOnly, formatDateTime} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";
import UserReports from "@/user/components/analytics/UserReports.vue";
import AppNotification from "@/shared/components/AppNotification.vue";
import API from "@/shared/helpers/API";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const superStore = useSuperStore();
		const scriptStore = useScriptStore();
		return {getString: languageStore.getString, superStore, readableEurValue: languageStore.readableEurValue, scriptStore};
	},
	name: "UserTablesAggregated",
	components: {AppNotification, UserReports, AppTable },
	props: {
		aggregatedData: {
			type: Object,
			default: null
		},
		businesses: {
			type: Array,
			default: () => []
		},
		periodDates: {
			type: Object,
			default: null
		},
		isAggregateOnly: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			pageSize: 10,
			currentPage: 1,
			sortField: "readable_gross_volume",
			sortAsc: false, // Default to descending sort
			selectedFilters: null,
			aggregateBusinessesData: [],
			aggregateTotalItems: 0,
			headingsStandard: [
				{
					text: this.getString('name'),
					fieldName: 'name',
					sortable: false,
					cols: 2,
				},
				{
					text: this.getString('orders_volume'),
					fieldName: 'orders',
					sortable: false,
					cols: 1,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('average_receipt'),
					fieldName: 'readable_average_receipt',
					sortable: false,
					cols: 1,
				},
			],
			headingsSuper: [
				{
					text: this.getString('net_volume'),
					fieldName: 'readable_net_volume',
					sortable: false,
					cols: 1,
				},
				{
					text: this.getString('gross_fee'),
					fieldName: 'readable_gross_fee',
					sortable: true,
					cols: 1,
				}
			],
			filtersStandard: [
				{
					type: "price",
					key: "readable_gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			filtersSuper: [{
				type: "price",
				key: "readable_gross_fee",
				text: this.getString('gross_fee'),
				active: false,
				value: null,
				activeItem: 0,
				finalValue: '',
			}]
		};
	},
	computed: {
		startDate() {
			return this.periodDates && this.periodDates.start ? formatDateOnly(this.periodDates.start) : null;
		},
		tableHeadings() {
			let headings = [...this.headingsStandard];
			// Show super headings (net volume, gross fee) for mega/ultra users
			if(this.superStore.isSuper) {
				headings.push(...this.headingsSuper);
			}
			return headings;
		},
		filters() {
			let filters = this.filtersStandard;
			if(this.superStore.isSuper) {
				filters.push(...this.filtersSuper);
			}
			return filters;
		},
		parsedBusinesses() {
			// For non-aggregate users, process the data from props
			if (this.isAggregateOnly || !this.aggregatedData) {
				return [];
			}
			
			let finalBusinesses = [];
			let ordersData = {};
			
			// Process aggregated data
			if (this.aggregatedData && this.aggregatedData.full) {
				this.aggregatedData.full.forEach(row => {
					const businessId = row.business_id;
					if (!ordersData[businessId]) {
						ordersData[businessId] = {
							orders: 0,
							total: 0,
							fee: 0,
							fee_net: 0,
						};
					}
					ordersData[businessId].orders += row.orders_cnt || 0;
					ordersData[businessId].total += (row.gross_sales || 0) / 100;
					// For mega/ultra users, use fee field if available
					if (row.fee !== undefined) {
						ordersData[businessId].fee += (row.fee || 0) / 100;
					}
				});
			}
			
			this.businesses.forEach(business => {
				let businessOrders = ordersData[business.business_id];
				let orders = businessOrders && businessOrders.orders ? businessOrders.orders : 0;
				let total = businessOrders && businessOrders.total ? businessOrders.total : 0;
				let fees = businessOrders && businessOrders.fee ? businessOrders.fee : 0;
				let fees_net = businessOrders && businessOrders.fee_net ? businessOrders.fee_net : 0;
				finalBusinesses.push({
					name: business && business.name ? business.name : '',
					orders: orders,
					gross_volume: total,
					net_volume: total - fees,
					gross_fee: fees,
					net_fee: fees_net,
					average_receipt: orders > 0 ? total/orders : 0,
					readable_gross_volume: this.readableEurValue(total),
					readable_net_volume: this.readableEurValue(total - fees),
					readable_average_receipt: this.readableEurValue(orders > 0 ? total/orders : 0),
					readable_gross_fee: this.readableEurValue(fees),
					readable_net_fee: this.readableEurValue(fees_net),
				})
			});

			finalBusinesses = this.filterBusinesses(finalBusinesses);
			if(this.sortField) {
				let sortField = this.sortField.startsWith('readable_') ? this.sortField.replace('readable_', '') : this.sortField;
				if(!this.sortAsc) {
					if(sortField === 'name') {
						finalBusinesses.sort((b,a) => a.name.localeCompare(b.name));
					} else {
						finalBusinesses.sort((b,a) => a[sortField] - b[sortField]);
					}
				} else {
					if(sortField === 'name') {
						finalBusinesses.sort((a, b) => a.name.localeCompare(b.name));
					} else {
						finalBusinesses.sort((a, b) => a[sortField] - b[sortField]);
					}
				}
			}
			return finalBusinesses;
		},
		currentShownBusinesses() {
			return this.parsedBusinesses.slice(
				(this.currentPage - 1) * this.pageSize,
				this.currentPage * this.pageSize
			);
		},
	},
	methods: {
		dummyFetchFunction(filtersObj, callback) {
			// For non-aggregate users, data is already loaded via props
			// Just call success callback
			callback(true);
		},
		fetchBusinessesPage(filtersObj, callback) {
			// Extract pagination info from AppTable's state
			const page = this.currentPage;
			const pageSize = this.pageSize;
			const sortField = this.sortField === 'readable_gross_volume' ? 'gross_sales' : 'fee';
			const sortDirection = this.sortAsc ? 'asc' : 'desc';
			
			// Convert filters
			const apiFilters = {};
			if (filtersObj.readable_gross_volume) {
				apiFilters.gross_sales = filtersObj.readable_gross_volume;
			}
			if (filtersObj.readable_gross_fee) {
				apiFilters.fee = filtersObj.readable_gross_fee;
			}
			
			// Call new paginated API
			const startDateDB = formatDateTime(this.periodDates.start);
			const endDateDB = formatDateTime(this.periodDates.end);
			
			API.init().superStatsBusinessesAggregated(
				startDateDB,
				endDateDB,
				page,
				pageSize,
				sortField,
				sortDirection,
				apiFilters,
				response => {
					if (response && response.result === "OK") {
						// Format data for table display
						this.aggregateBusinessesData = this.formatBusinessData(response.businesses);
						// Keep using frontend count since we have all businesses already
						callback(true);
					} else {
						this.aggregateBusinessesData = [];
						callback(false, "Failed to fetch data");
					}
				}
			);
		},
		formatBusinessData(businesses) {
			// Create a map of business_id to business for quick lookup
			const businessMap = {};
			if (this.superStore.businesses) {
				this.superStore.businesses.forEach(b => {
					businessMap[b.business_id] = b;
				});
			}
			
			return businesses.map(business => {
				// Look up the business name from our local data
				const businessInfo = businessMap[business.business_id];
				const businessName = businessInfo ? businessInfo.name : `Business ${business.business_id}`;
				
				return {
					name: businessName,
					orders: business.orders_cnt || 0,
					gross_volume: (business.gross_sales || 0) / 100,
					net_volume: ((business.gross_sales || 0) - (business.fee || 0)) / 100,
					gross_fee: (business.fee || 0) / 100,
					net_fee: 0, // Not available in this context
					average_receipt: business.orders_cnt > 0 ? (business.gross_sales || 0) / 100 / business.orders_cnt : 0,
					readable_gross_volume: this.readableEurValue((business.gross_sales || 0) / 100),
					readable_net_volume: this.readableEurValue(((business.gross_sales || 0) - (business.fee || 0)) / 100),
					readable_average_receipt: this.readableEurValue(business.orders_cnt > 0 ? (business.gross_sales || 0) / 100 / business.orders_cnt : 0),
					readable_gross_fee: this.readableEurValue((business.fee || 0) / 100),
					readable_net_fee: this.readableEurValue(0),
				};
			});
		},
		processAggregatedDataByBusiness(aggregatedData, businesses, filters) {
			const businessMap = {};
			businesses.forEach(b => {
				businessMap[b.business_id] = b;
			});
			
			const ordersData = {};
			
			// Process aggregated data
			if (aggregatedData && aggregatedData.full) {
				// Check if data has business_id field
				const hasBusinessId = aggregatedData.full.length > 0 && 'business_id' in aggregatedData.full[0];
				
				if (hasBusinessId) {
					// Normal case - group by business_id
					aggregatedData.full.forEach(row => {
						const businessId = row.business_id;
						if (!ordersData[businessId]) {
							ordersData[businessId] = {
								orders: 0,
								total: 0,
								fee: 0,
								fee_net: 0,
							};
						}
						ordersData[businessId].orders += row.orders_cnt || 0;
						ordersData[businessId].total += (row.gross_sales || 0) / 100;
						// For mega/ultra users, use fee field if available
						if (row.fee !== undefined) {
							ordersData[businessId].fee += (row.fee || 0) / 100;
						}
					});
				} else {
					// No business_id - data is aggregated across all businesses
					// This should not happen with our current approach (always sending specific business IDs)
					// But handle it gracefully by distributing equally
					const totalOrders = aggregatedData.full.reduce((sum, row) => sum + (row.orders_cnt || 0), 0);
					const totalSales = aggregatedData.full.reduce((sum, row) => sum + (row.gross_sales || 0), 0) / 100;
					const totalFees = aggregatedData.full.reduce((sum, row) => sum + (row.fee || 0), 0) / 100;
					
					businesses.forEach(business => {
						ordersData[business.business_id] = {
							orders: Math.round(totalOrders / businesses.length),
							total: totalSales / businesses.length,
							fee: totalFees / businesses.length,
							fee_net: 0,
						};
					});
				}
			}
			
			let finalBusinesses = [];
			businesses.forEach(business => {
				let businessOrders = ordersData[business.business_id];
				let orders = businessOrders && businessOrders.orders ? businessOrders.orders : 0;
				let total = businessOrders && businessOrders.total ? businessOrders.total : 0;
				let fees = businessOrders && businessOrders.fee ? businessOrders.fee : 0;
				let fees_net = businessOrders && businessOrders.fee_net ? businessOrders.fee_net : 0;
				
				finalBusinesses.push({
					name: business && business.name ? business.name : '',
					orders: orders,
					gross_volume: total,
					net_volume: total - fees,
					gross_fee: fees,
					net_fee: fees_net,
					average_receipt: orders > 0 ? total/orders : 0,
					readable_gross_volume: this.readableEurValue(total),
					readable_net_volume: this.readableEurValue(total - fees),
					readable_average_receipt: this.readableEurValue(orders > 0 ? total/orders : 0),
					readable_gross_fee: this.readableEurValue(fees),
					readable_net_fee: this.readableEurValue(fees_net),
				});
			});
			
			// Apply filters if provided
			if (filters) {
				finalBusinesses = this.filterBusinesses(finalBusinesses, filters);
			}
			
			return finalBusinesses;
		},
		changeSort(newSortField, newSortAsc) {
			this.currentPage = 1;
			this.sortField = newSortField;
			this.sortAsc = !!newSortAsc;
			if (this.$refs['businesses-table'] && this.$refs['businesses-table'].$refs['pagination-btns']) {
				this.$refs['businesses-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeFilter(newFilters) {
			this.currentPage = 1;
			this.selectedFilters = newFilters;
			if (this.$refs['businesses-table'] && this.$refs['businesses-table'].$refs['pagination-btns']) {
				this.$refs['businesses-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		filterBusinesses(businessesArray, filters = null) {
			const activeFilters = filters || this.selectedFilters;
			if(!businessesArray || !businessesArray.length || !activeFilters || !Object.values(activeFilters).length) {
				return businessesArray;
			}
			for(let filterField in activeFilters) {
				let filterValue = activeFilters[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if(filterValue && ['gross_volume', 'gross_fee'].includes(filterName)) {
					if(filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						if(filterName !== 'orders') { finalValue /= 100 }
						businessesArray = businessesArray.filter(b=>b[filterName] < finalValue);
					} else if(filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						if(filterName !== 'orders') { finalValue /= 100 }
						businessesArray = businessesArray.filter(b=>b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						if(filterName !== 'orders') { finalValue /= 100 }
						businessesArray = businessesArray.filter(b=>b[filterName] === finalValue);
					}
				}
			}
			return businessesArray;
		},
		exportBusinesses(expExtension) {
			// For mega/ultra users, we need to fetch all data first
			if (this.isAggregateOnly) {
				// Fetch all businesses data using the new API with a large page size
				const startDateDB = formatDateTime(this.periodDates.start);
				const endDateDB = formatDateTime(this.periodDates.end);
				
				// Request all businesses in one page for export
				API.init().superStatsBusinessesAggregated(
					startDateDB,
					endDateDB,
					1, // page
					10000, // large page size to get all
					'gross_sales', // sort by gross sales
					'desc', // descending
					{}, // no filters for export
					response => {
						if (response && response.result === "OK" && response.businesses) {
							const allBusinessesData = this.formatBusinessData(response.businesses);
							this.performExport(allBusinessesData, expExtension);
						} else {
							this.$refs['table-no-data'].openNotification();
						}
					}
				);
			} else {
				// For normal users, use parsed businesses
				this.performExport(this.parsedBusinesses, expExtension);
			}
		},
		performExport(businessesData, expExtension) {
			let columns = [
				{ header: this.getString('name'), key: 'name' },
				{ header: this.getString('quantity'), key: 'orders', type: 'number' },
				{ header: this.getString('gross_volume') + " €", value: b => Number(b.gross_volume.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
				{ header: this.getString('average_receipt') + " €", value: b => Number(b.average_receipt.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
			];
			if (this.superStore.isSuper) {
				columns.push(
					{ header: this.getString('net_volume') + " €", value: b => Number(b.net_volume.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('gross_fee') + " €", value: b => Number(b.gross_fee.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('net_fee') + " €", value: b => Number(b.net_fee.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
				);
			}
			let fileName = this.getString("businesses").toLowerCase()+"_"+this.startDate.split(" ")[0];
			exportToFile({
				columns,
				rows: businessesData,
				fileName,
				sheetName: this.getString("buisinesses").toLowerCase(),
				format: expExtension,
				onEmpty: () => this.$refs['table-no-data'].openNotification(),
			});
		},
	},
	mounted() {
		// If aggregate only, set the total items count from all available businesses
		if (this.isAggregateOnly) {
			// Use all businesses from superStore, not just selected ones
			this.aggregateTotalItems = this.superStore.businesses ? this.superStore.businesses.length : 0;
		}
	},
	watch: {
		periodDates: {
			handler(newVal, oldVal) {
				if (newVal && oldVal && (newVal.start !== oldVal.start || newVal.end !== oldVal.end)) {
					// Reset to first page when dates change
					this.currentPage = 1;
					// Refresh the table data
					if (this.$refs['businesses-table']) {
						this.$refs['businesses-table'].fetchData();
					}
				}
			},
			deep: true
		}
	},
}
</script>

<style lang="scss" scoped>
.tables-wrapper {
	display: flex;
	flex-direction: column;
	width: 100%;
}
</style>